import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TitleHelp } from '@/components/ui/TitleHelp';
import { Sidebar } from './Sidebar';
import { IconRail } from './IconRail';
import { useNavTree } from './useNavTree';
import { NavTabs } from './NavTabs';
import { useAuth } from '@/lib/auth/auth-context';
import { UserType } from '@/lib/auth/permissions';
import { ensureBugReporter } from '@/lib/bug-reporter';
import { readSidebarCollapsed, writeSidebarCollapsed } from '@/lib/sidebar-collapse';
import { watchStickyList } from '@/lib/sticky-list';
import { isEmbedded } from '@/lib/page-window';
import { PageWindowHost } from './PageWindowHost';
import { cn } from '@/lib/utils';

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
  /** GEN-04-06: the page's explanation, shown behind a '?' beside the title. */
  help?: React.ReactNode;
}

// My Profile (Angela, 22 Oct 2026) shows whole pages — My Leave, My Pay,
// Claim Submission, Review Submission — inside its tabs. A page drawn inside
// another page's frame shows only its content, never a second sidebar/header.
const InsideAppLayout = createContext(false);

export function AppLayout(props: AppLayoutProps) {
  const nested = useContext(InsideAppLayout);
  if (nested) return <>{props.children}</>;
  return (
    <InsideAppLayout.Provider value={true}>
      <AppLayoutFrame {...props} />
    </InsideAppLayout.Provider>
  );
}

function AppLayoutFrame({ children, title, help }: AppLayoutProps) {
  const { t } = useTranslation('nav');
  const { user } = useAuth();
  const embedded = isEmbedded();
  const nav = useNavTree();
  // GEN-04-04 (7): collapsed or open is remembered per user until sign-out.
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => readSidebarCollapsed(user?.id));

  const toggleSidebar = () => {
    setIsSidebarCollapsed((collapsed) => {
      writeSidebarCollapsed(user?.id, !collapsed);
      return !collapsed;
    });
  };

  // Initialise the shared @haskytech/bug-reporter SDK once the user is known;
  // the "Report a bug" entry in the profile menu only opens it.
  useEffect(() => {
    if (user) {
      void ensureBugReporter({
        email: user.email || '',
        role: (user.userType || 'RECEPTIONIST') as UserType,
      });
    }
  }, [user]);

  // GEN-04-05 / GEN-04-06 — the path in small grey, the title under it. The
  // path is the menu trail; a page that is not a menu item ends the path with
  // its own title ('Customers › Customer list › Customer Details').
  // A page that is one of a menu entry's tabs shows that entry's tab row.
  const tabGroup = [...nav.trail].reverse().find((node) => node.tabs && node.tabs.length > 0) ?? null;
  const crumbs = nav.trail.map((node) => t(node.labelKey));
  const leaf = nav.trail[nav.trail.length - 1];
  const isMenuPage = Boolean(leaf && nav.isActive(leaf));
  if (title && !isMenuPage && crumbs[crumbs.length - 1] !== title) crumbs.push(title);

  // GEN-04-21 (Angela, 21 Oct 2026): the path and title stay at the top while
  // the page scrolls, on every page. Their height is published as
  // --page-heading-h so a page's own tab row can stick right under them
  // (`sticky top-[var(--page-heading-h)]`).
  const mainRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const heading = headingRef.current;
    const main = mainRef.current;
    if (!heading || !main || typeof ResizeObserver === 'undefined') return;
    const publish = () => main.style.setProperty('--page-heading-h', `${heading.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(heading);
    return () => observer.disconnect();
  }, []);

  // GEN-04-23: the filter box and the list's header row stick under them.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const main = mainRef.current;
    const content = contentRef.current;
    if (!main || !content) return;
    return watchStickyList(main, content);
  }, []);

  return (
    // GEN-04-07 (Angela, 20 Oct 2026): like Studio Manager, the layout never
    // rearranges on a narrow window — the top menus and the sidebar stay, and
    // the page scrolls sideways below 1000 px instead; every top menu fits
    // on one line from 1000 px (GEN-04-19).
    <div className={cn('flex h-screen bg-background', !embedded && 'min-w-[1000px]')}>
      {/* In a page window (lib/page-window.ts) the screen draws without the
          rail and the menu panel; the window itself is drawn by PageWindowHost.
          GEN-04-02 / GEN-04-04 (design N1): dark icon rail · white menu panel · page. */}
      {!embedded && <IconRail nav={nav} />}
      {!embedded && <PageWindowHost />}
      <div className="flex min-h-0 min-w-0 flex-1">
        {!embedded && <Sidebar nav={nav} isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />}
        <main ref={mainRef} className="flex-1 overflow-auto px-4 pb-4 md:px-6 md:pb-6">
          <div ref={contentRef} className="mx-auto max-w-[1200px]">
            {(title || crumbs.length > 0) && (
              <div
                ref={headingRef}
                className="sticky top-0 z-30 mb-4 bg-background pb-2 pt-4 md:pt-6"
                data-testid="page-heading"
              >
                {crumbs.length > 0 && (
                  <p className="text-xs text-[#9CA3AF]" data-testid="page-path">
                    {crumbs.join(' › ')}
                  </p>
                )}
                {title && (
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-bold text-[#1F2937]">{title}</h1>
                    {help && <TitleHelp label={t('help')}>{help}</TitleHelp>}
                  </div>
                )}
              </div>
            )}
            {!(title || crumbs.length > 0) && <div className="h-4 md:h-6" />}
            {tabGroup && <NavTabs group={tabGroup} isActive={nav.isActive} />}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
